import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { updateTicketTypeSetting } from '@/lib/businessValueApi';
import { filterBusinessValueSettings } from '@/lib/businessValueCategories';
import { usePermissions } from '@/contexts/PermissionsContext';
import { permissionDeniedTitle } from '@/lib/permissionUi';
import { toast } from 'sonner';
import { Save } from 'lucide-react';

export default function TicketTypeSettingsPanel({ settings, onSaved }) {
  const { hasPermission } = usePermissions();
  const canUpdate = hasPermission('business-value', 'update');
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(null);
  const rows = filterBusinessValueSettings(settings);

  const toNumber = (value, fallback) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const getDraft = (row) => {
    const key = row.ticket_type_key;
    const saved = drafts[key];
    return {
      estimated_hours: saved?.estimated_hours ?? row.estimated_hours,
      hourly_bill_rate: saved?.hourly_bill_rate ?? row.hourly_bill_rate,
    };
  };

  const setDraftField = (row, field, value) => {
    const key = row.ticket_type_key;
    setDrafts((prev) => {
      const base = prev[key] || {
        estimated_hours: row.estimated_hours,
        hourly_bill_rate: row.hourly_bill_rate,
      };
      return {
        ...prev,
        [key]: { ...base, [field]: value },
      };
    });
  };

  const handleSave = async (row) => {
    if (!canUpdate) return;
    const key = row.ticket_type_key;
    const draft = getDraft(row);
    setSaving(key);
    try {
      await updateTicketTypeSetting(key, {
        display_name: row.display_name,
        estimated_hours: toNumber(draft.estimated_hours, row.estimated_hours),
        hourly_bill_rate: toNumber(draft.hourly_bill_rate, row.hourly_bill_rate),
      });
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      toast.success(`Saved ${row.display_name}`);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(null);
    }
  };

  if (!rows.length) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          No ticket type settings found. Apply the business value schema migration.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="bv-settings-panel">
      <CardHeader>
        <CardTitle>Ticket type effort &amp; billing</CardTitle>
        <CardDescription>
          Set estimated manual hours and hourly bill rate per ticket category for savings calculations.
        </CardDescription>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-muted-foreground">
              <th className="pb-2 pr-4">Ticket type</th>
              <th className="pb-2 pr-4">Est. hours</th>
              <th className="pb-2 pr-4">Bill rate ($/hr)</th>
              <th className="pb-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const draft = getDraft(row);
              return (
                <tr key={row.ticket_type_key} className="border-b last:border-0">
                  <td className="py-3 pr-4 font-medium">{row.display_name}</td>
                  <td className="py-3 pr-4">
                    <Input
                      type="number"
                      min="0"
                      step="0.5"
                      className="w-24"
                      value={draft.estimated_hours}
                      disabled={!canUpdate}
                      onChange={(e) => setDraftField(row, 'estimated_hours', e.target.value)}
                    />
                  </td>
                  <td className="py-3 pr-4">
                    <Input
                      type="number"
                      min="0"
                      step="1"
                      className="w-28"
                      value={draft.hourly_bill_rate}
                      disabled={!canUpdate}
                      onChange={(e) => setDraftField(row, 'hourly_bill_rate', e.target.value)}
                    />
                  </td>
                  <td className="py-3">
                    <span title={permissionDeniedTitle(canUpdate, 'save ticket type pricing')}>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!canUpdate || saving === row.ticket_type_key}
                        onClick={() => handleSave(row)}
                      >
                        <Save className="h-4 w-4 mr-1" />
                        Save
                      </Button>
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
